/**
 * Power Visualization Component - Tree view of device hierarchy
 * 
 * AI Agent Note: This component renders the device tree with power flow visualization.
 * Uses recursive rendering to display parent-child relationships.
 * Imports icons and labels from config/constants.
 * Power calculations use utils/powerCalculations for consistency.
 */

import React, { useState } from 'react';
import { Badge, Tooltip, Card, Flex, Text, Heading, Box, Section } from '@radix-ui/themes';
import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { Device, ValidationError } from '../../types';
import { DEVICE_TYPE_LABELS } from '../../config/constants';
import DeviceIcon from '../DeviceIcon';
import { useDevices } from '../../contexts/DeviceContext';
import { getRootDevices } from '../../utils/deviceTree';
import { calculateTotalPowerDraw, formatPower, calculateAmps, formatAmps, getDeviceVoltage, isPassthroughDevice } from '../../utils/powerCalculations';
import { getDeviceIssues, hasDeviceErrors, hasDeviceWarnings } from '../../contexts/deviceValidation';
import './PowerVisualization.css';

interface PowerVisualizationProps {
  onEditDevice: (device: Device) => void;
  issues: ValidationError[];
}

const PowerVisualization: React.FC<PowerVisualizationProps> = ({ onEditDevice, issues }) => {
  const { devices, getConnectedDevices } = useDevices();
  const [collapsedDevices, setCollapsedDevices] = useState<Set<string>>(new Set());

  // Find root devices (power sources or unconnected devices)
  const rootDevices = getRootDevices(devices);

  // Toggle collapse state for a device
  const toggleCollapse = (deviceId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setCollapsedDevices(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) {
        next.delete(deviceId);
      } else {
        next.add(deviceId);
      }
      return next;
    });
  };

  // Calculate battery runtime in hours
  const calculateBatteryRuntime = (device: Device): number | null => {
    if (!device.batteryCapacity?.capacityWh || device.batteryCapacity.capacityWh <= 0) {
      return null;
    }
    const totalDraw = calculateTotalPowerDraw(device.id, devices);
    if (totalDraw <= 0) return null;
    return device.batteryCapacity.capacityWh / totalDraw;
  };

  // Format runtime for display
  const formatRuntime = (hours: number): string => {
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = Math.floor(hours % 24);
      return `${days}d ${remainingHours}h`;
    } else if (hours >= 1) {
      const h = Math.floor(hours);
      const m = Math.floor((hours - h) * 60);
      return `${h}h ${m}m`;
    } else {
      const m = Math.floor(hours * 60);
      return `${m}m`;
    }
  };

  const renderDeviceNode = (device: Device, level: number = 0): React.ReactNode => {
    const children = getConnectedDevices(device.id);
    const totalPowerDraw = calculateTotalPowerDraw(device.id, devices);
    const deviceIssues = getDeviceIssues(device.id, issues);
    const hasError = hasDeviceErrors(device.id, issues);
    const hasWarning = hasDeviceWarnings(device.id, issues);
    
    // Calculate power properly for passthrough vs regular devices
    const isPassthrough = isPassthroughDevice(device);
    let childrenPowerDraw = 0;
    let deviceSelfPower = device.powerRequirement;
    
    if (isPassthrough) {
      // For passthrough devices: children's power + 1% overhead
      children.forEach(child => {
        childrenPowerDraw += calculateTotalPowerDraw(child.id, devices);
      });
      deviceSelfPower = childrenPowerDraw * 0.01; // 1% overhead
    } else {
      // For regular devices: total - own consumption = children
      childrenPowerDraw = totalPowerDraw - device.powerRequirement;
    }
    
    const voltage = getDeviceVoltage(device);
    const ownAmps = calculateAmps(deviceSelfPower, voltage);
    const childrenAmps = calculateAmps(childrenPowerDraw, voltage);
    const totalAmps = calculateAmps(totalPowerDraw, voltage);
    
    // Calculate utilization percentage for color coding
    const utilizationPercent = device.passthroughPower > 0 
      ? (childrenPowerDraw / device.passthroughPower) * 100 
      : 0;
    
    // Determine color based on utilization
    const getUtilizationColor = () => {
      if (utilizationPercent >= 90) return 'red';
      if (utilizationPercent >= 75) return 'orange';
      if (utilizationPercent >= 50) return 'yellow';
      return 'green';
    };
    
    // Get connection info (which port is being used)
    const getConnectionBadge = () => {
      if (!device.connectedTo && !device.usbConnection) return { power: null, usb: null };
      
      const badges = { power: null as React.ReactNode, usb: null as React.ReactNode };
      
      // Power connection badge
      if (device.powerConnection) {
        const sourceDevice = devices.find(d => d.id === device.powerConnection?.sourceDeviceId);
        if (sourceDevice?.powerOutputs) {
          const output = sourceDevice.powerOutputs[device.powerConnection.outputIndex];
          if (output) {
            // Calculate which physical port number (1-indexed for display)
            // Start with ports from previous output groups, then add the portIndex within current group
            let portNumber = 1;
            for (let i = 0; i < device.powerConnection.outputIndex; i++) {
              portNumber += sourceDevice.powerOutputs[i].portCount;
            }
            portNumber += device.powerConnection.portIndex; // Add the port index within the current group
            
            // Check if power source is different from parent device
            const isDifferentSource = device.connectedTo !== device.powerConnection.sourceDeviceId;
            const label = isDifferentSource 
              ? `⚡ ${sourceDevice.name} Port ${portNumber}` 
              : `⚡ Port ${portNumber} (${output.voltage}V)`;
            
            badges.power = (
              <Tooltip key="power" content={`Power from ${sourceDevice.name} Port ${portNumber}: ${output.voltage}V @ ${output.maxCurrent}A`}>
                <Badge color="violet" variant="soft" size="1" style={{ cursor: 'help' }}>
                  {label}
                </Badge>
              </Tooltip>
            );
          }
        }
      }
      
      // USB connection badge
      if (device.usbConnection) {
        const sourceDevice = devices.find(d => d.id === device.usbConnection?.sourceDeviceId);
        if (sourceDevice?.usbOutputs) {
          const output = sourceDevice.usbOutputs[device.usbConnection.outputIndex];
          if (output) {
            // Calculate which physical port number (1-indexed for display)
            // Start with ports from previous output groups, then add the portIndex within current group
            let portNumber = 1;
            for (let i = 0; i < device.usbConnection.outputIndex; i++) {
              portNumber += sourceDevice.usbOutputs[i].count;
            }
            portNumber += device.usbConnection.portIndex; // Add the port index within the current group
            
            // Check if USB source is different from parent device
            const isDifferentSource = device.connectedTo !== device.usbConnection.sourceDeviceId;
            const label = isDifferentSource 
              ? `🔌 ${sourceDevice.name} Port ${portNumber} (${output.version})` 
              : `🔌 Port ${portNumber} (${output.version})`;
            
            badges.usb = (
              <Tooltip key="usb" content={`USB from ${sourceDevice.name} Port ${portNumber}: ${output.version} ${output.powered ? '(Powered)' : '(Data Only)'}`}>
                <Badge color="blue" variant="soft" size="1" style={{ cursor: 'help' }}>
                  {label}
                </Badge>
              </Tooltip>
            );
          }
        }
      }
      
      return badges;
    };

    const isCollapsed = collapsedDevices.has(device.id);
    const batteryRuntime = device.batteryCapacity ? calculateBatteryRuntime(device) : null;
    const connectionBadges = getConnectionBadge();

    return (
      <div key={device.id} className="tree-node" style={{ marginLeft: level * 20 }}>
        <div 
          className={`node-content ${hasError ? 'error' : hasWarning ? 'warning' : ''}`}
          onClick={() => onEditDevice(device)}
        >
          {children.length > 0 && (
            <div 
              className="collapse-toggle" 
              onClick={(e) => toggleCollapse(device.id, e)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginRight: '4px' }}
            >
              {isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </div>
          )}
          <div className="node-icon"><DeviceIcon type={device.type} size={28} /></div>
          <div className="node-info">
            <div className="node-name" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span>{device.name}</span>
              {batteryRuntime !== null && batteryRuntime > 0 && (
                <Tooltip content={`Estimated runtime: ${formatRuntime(batteryRuntime)} based on ${formatPower(totalPowerDraw)}W total draw from ${device.batteryCapacity?.capacityWh}Wh battery capacity`}>
                  <Badge color="green" variant="soft" size="1" style={{ cursor: 'help' }}>
                    🔋 {formatRuntime(batteryRuntime)}
                  </Badge>
                </Tooltip>
              )}
            </div>
            <div className="node-type" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span>{DEVICE_TYPE_LABELS[device.type]}</span>
              {connectionBadges.power}
              {connectionBadges.usb}
            </div>
          </div>
          <div className="node-power">
            {/* For power sources and distribution: show available power */}
            {(device.type === 'power-source' || device.type === 'power-box') && device.passthroughPower > 0 ? (
              <>
                <Tooltip content={`Maximum Power Draw: ${formatPower(childrenPowerDraw)}W of ${device.passthroughPower}W available (${utilizationPercent.toFixed(1)}% utilized) - Shows worst-case scenario when all devices run at peak consumption`}>
                  <div className="power-draw" style={{ cursor: 'help' }}>
                    <span className={`power-value usage-${getUtilizationColor()}`}>
                      {formatPower(childrenPowerDraw)}W
                    </span>
                    <span className="power-separator">/</span>
                    <span className="power-value available">{device.passthroughPower}W</span>
                  </div>
                </Tooltip>
                <Tooltip content={`Maximum Current Draw: ${formatAmps(childrenAmps)}A of ${device.maxTotalCurrent ? `${device.maxTotalCurrent}A total limit` : `${formatAmps(calculateAmps(device.passthroughPower, voltage))}A available @ ${voltage}V`} - Worst-case peak load`}>
                  <div className="power-amps" style={{ cursor: 'help' }}>
                    <span className={`amps-value usage-${getUtilizationColor()}`}>
                      {formatAmps(childrenAmps)}A
                    </span>
                    <span className="power-separator">/</span>
                    <span className="amps-value available">{device.maxTotalCurrent ? `${device.maxTotalCurrent}A` : `${formatAmps(calculateAmps(device.passthroughPower, voltage))}A`}</span>
                  </div>
                </Tooltip>
                {deviceSelfPower > 0 && (
                  <Tooltip content={`Device overhead: approximately 1% of connected load for passthrough operation`}>
                    <div className="power-self" style={{ cursor: 'help' }}>Self: {formatPower(deviceSelfPower)}W ({formatAmps(ownAmps)}A)</div>
                  </Tooltip>
                )}
                {/* Show total current limit if device has one
                {device.maxTotalCurrent && device.maxTotalCurrent > 0 && (
                  <Tooltip content={`Total current limit across all outputs: ${childrenAmps.toFixed(2)}A of ${device.maxTotalCurrent}A used (${((childrenAmps / device.maxTotalCurrent) * 100).toFixed(1)}% of total capacity)`}>
                    <div className="power-total-current" style={{ cursor: 'help', fontSize: '0.75rem', color: childrenAmps > device.maxTotalCurrent ? '#dc3545' : '#888' }}>
                      Total: {formatAmps(childrenAmps)}A / {device.maxTotalCurrent}A
                    </div>
                  </Tooltip>
                )} */}
              </>
            ) : (
              /* For regular devices: show their consumption */
              <>
                <Tooltip content={`Maximum power consumption: ${formatPower(device.powerRequirement)}W (worst-case peak load)`}>
                  <div className="power-draw" style={{ cursor: 'help' }}>
                    <span className="power-value">{formatPower(device.powerRequirement)}W</span>
                  </div>
                </Tooltip>
                <Tooltip content={`Maximum current draw at operating voltage: ${formatAmps(ownAmps)}A @ ${voltage}V (peak load)`}>
                  <div className="power-amps" style={{ cursor: 'help' }}>{formatAmps(ownAmps)}A @ {voltage}V</div>
                </Tooltip>
                {children.length > 0 && (
                  <Tooltip content={`Total power including all connected devices: ${formatPower(totalPowerDraw)}W (${formatAmps(totalAmps)}A)`}>
                    <div className="power-total" style={{ cursor: 'help' }}>Total: {formatPower(totalPowerDraw)}W ({formatAmps(totalAmps)}A)</div>
                  </Tooltip>
                )}
              </>
            )}
          </div>
          {deviceIssues.length > 0 && (
            <div className="node-issue-indicator" title={deviceIssues.map(i => i.message).join('\n')}>
              {hasError ? '❌' : '⚠️'}
            </div>
          )}
        </div>
        {children.length > 0 && !isCollapsed && (
          <div className="tree-children">
            {children.map(child => renderDeviceNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (devices.length === 0) {
    return (
      <Section size="3">
        <Card size="4">
          <Flex direction="column" align="center" gap="4" p="6">
            <Text size="9" style={{ fontSize: '4rem' }}>🔭</Text>
            <Heading size="6">Welcome to AstroPower Manager!</Heading>
            <Text color="gray" size="3" mb="4">Get started by building your astrophotography rig power visualization.</Text>
            
            <Card style={{ maxWidth: '600px', width: '100%' }} variant="surface">
              <Flex direction="column" gap="3" p="4">
                <Heading size="4" color="violet">📋 Quick Start Guide:</Heading>
                <Box asChild>
                  <ol style={{ paddingLeft: '20px', margin: '10px 0' }}>
                    <li><strong>Add Power Source:</strong> Click "+ Add Device" and select your power supply (PSU, battery, or AC adapter)</li>
                    <li><strong>Add Equipment:</strong> Add your cameras, mounts, focusers, and other devices</li>
                    <li><strong>Connect Devices:</strong> Link equipment to power sources and select specific ports</li>
                    <li><strong>Monitor Usage:</strong> View real-time power consumption and available capacity</li>
                  </ol>
                </Box>
          
                <Card variant="surface" style={{ backgroundColor: 'var(--amber-3)', border: '1px solid var(--amber-7)' }}>
                  <Text size="2">
                    <strong>⚠️ Important:</strong> All power calculations show <strong>maximum/worst-case consumption</strong> to ensure your power system can handle peak loads. Actual power draw may be lower during normal operation.
                  </Text>
                </Card>
          
                <Box>
                  <Text size="2" color="gray" weight="bold">💡 Tips:</Text>
                  <Box asChild mt="1">
                    <ul style={{ paddingLeft: '20px', margin: '5px 0' }}>
                      <li>Hover over values for detailed tooltips</li>
                      <li>Click devices to edit or view connection details</li>
                      <li>Click chevrons (▼) to collapse/expand device groups</li>
                      <li>Battery-powered sources show estimated runtime</li>
                    </ul>
                  </Box>
                </Box>
              </Flex>
            </Card>
          </Flex>
        </Card>
      </Section>
    );
  }

  const totalPowerDraw = devices.reduce((sum, d) => sum + d.powerRequirement, 0);

  return (
    <Section size="1" className="power-visualization">
      <Flex direction="column" gap="4">
        <Flex justify="between" align="center" wrap="wrap" gap="3">
          <Heading size="6">Power Flow Diagram</Heading>
          <Flex gap="4" align="center" wrap="wrap">
            <Badge color="gray" size="2" variant="soft">
              <strong>{devices.length}</strong> devices
            </Badge>
            <Badge color="violet" size="2" variant="soft">
              <strong>{formatPower(totalPowerDraw)}W</strong> max draw
            </Badge>
            <Tooltip content="Power sources and distribution devices show current usage vs. total available capacity. All values represent MAXIMUM/WORST-CASE power consumption to ensure your system can handle peak loads.">
              <Badge color="amber" size="2" variant="soft" style={{ cursor: 'help' }}>
                💡 Usage / Available (Max)
              </Badge>
            </Tooltip>
          </Flex>
        </Flex>
        
        <Card className="tree-container">
          {rootDevices.map(device => renderDeviceNode(device))}
        </Card>

        {issues.length > 0 && (
          <Card variant="surface" style={{ backgroundColor: 'var(--red-3)', border: '1px solid var(--red-7)' }}>
            <Flex direction="column" gap="2" p="3">
              <Heading size="4">⚠️ Issues Detected ({issues.length})</Heading>
              <Box asChild>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {issues.map((issue, idx) => (
                    <li key={idx} className={`issue-item ${issue.severity}`}>
                      <Text size="2">{issue.message}</Text>
                    </li>
                  ))}
                </ul>
              </Box>
            </Flex>
          </Card>
        )}
      </Flex>
    </Section>
  );
};

export default PowerVisualization;
